import Campaign from '../store/Campaign';
import React from 'react';
import { CAMPAIGN_DISMISSED } from '../utils/eventNames';
import { CampaignEndSignal } from '../store/schema/CampaignEndSignal';
import { CampaignQueue } from '../store/CampaignQueue';
import { CampaignType } from '../store/schema/CampaignType';
import { endCampaign } from '../utils/endCampaign';
import { getDataLogger } from 'telemetry';
import { getIconImageSource } from './utils/getImageSourceUtil';
import { getModifiedViewProps } from './utils/getModifiedViewProps';
import { getNetworkInfo } from 'mini-network-info';
import { LazyInfoModalWrapper } from 'mini-info-modal';
import { VideoSrc } from 'mini-info-modal/lib/components/InfoModal';

/**
 * @description Config which is required to create Modal Campaign Type instance
 * @param title: Title of the campaignType component
 * @param subtitle: Subtitle of the campaignType component
 * @param imageSrc: Image source of the campaignType component
 * @param actionButtonText: Customized action button text
 */
export interface CampaignModalConfig {
    title: string;
    subtitle: string;
    videoSrc?: VideoSrc[];
    imageSrc?: string;
    primaryButtonText?: string;
    elementToFocusOnClose?: React.RefObject<any>;
    onRender?: () => void;
}

const CAMPAIGN_MODAL_RENDERED = 'CampaignModalRendered';

/**
 * Class to instantiate modal campaign type object.
 */
export class CampaignModal implements CampaignType<CampaignModalConfig> {
    private static modalQueue = CampaignQueue.getInstance();

    public start(campaign: Campaign<CampaignModalConfig>) {
        CampaignModal.modalQueue.addToQueue(campaign);
    }

    public end(campaign: Campaign<CampaignModalConfig>, endSignal: CampaignEndSignal) {
        endCampaign(campaign, endSignal);
    }

    private onDismiss(campaign: Campaign<CampaignModalConfig>, endSignal: CampaignEndSignal) {
        const logger = getDataLogger();
        logger?.(CAMPAIGN_DISMISSED, { id: campaign.getRemoteCampaignId(), isUIAction: true });
        this.end(campaign, endSignal);
    }

    /**
     * This is called from the render function of the Campaign object
     * @param campaign Campaign object instance
     */
    public render(
        campaign: Campaign<CampaignModalConfig>,
        campaignTypeProps: CampaignModalConfig
    ): JSX.Element {
        const self = this;
        const onClick = () => self.onDismiss(campaign, CampaignEndSignal.OnUserDismiss);
        const networkInfo = getNetworkInfo();
        const { videoSrc, onRender } = campaignTypeProps;
        onRender?.();

        // Here when networkInfo is returned null then we assume network to be good as we have decided to show video experience in those scenarios.
        const isNetworkGood = networkInfo ? networkInfo.rtt <= 300 : true;

        const logger = getDataLogger();
        logger?.(CAMPAIGN_MODAL_RENDERED, {
            id: campaign.getRemoteCampaignId(),
            videoShown: isNetworkGood,
            rtt: networkInfo && networkInfo.rtt,
        });

        return (
            <LazyInfoModalWrapper
                {...campaignTypeProps}
                {...getModifiedViewProps(campaignTypeProps)}
                imageSrc={getIconImageSource(campaignTypeProps)}
                videoSrc={isNetworkGood && videoSrc}
                onOverlayClick={onClick}
                onClick={onClick}
            />
        );
    }
}
